import {
  OPERATOR_TYPES_1,
  OPERATOR_TYPES_2,
  OPERATOR_TYPES_3,
  OPERATOR_TYPES_4,
  OPERATOR_TYPES_7,
  OPERATOR_TYPES_9,
} from 'dashboard/routes/dashboard/settings/automation/operators';
import {
  DEFAULT_MESSAGE_CREATED_CONDITION,
  DEFAULT_CONVERSATION_CONDITION,
  DEFAULT_OTHER_CONDITION,
  DEFAULT_PLANNING_EVENT_CONDITION,
  DEFAULT_ACTIONS,
} from 'dashboard/constants/automation';
import filterQueryGenerator from './filterQueryGenerator';
import actionQueryGenerator from './actionQueryGenerator';
import { CUSTOM_TEMPLATE_ACTION_PREFIX } from 'dashboard/routes/dashboard/settings/automation/constants';

export const getCustomAttributeInputType = key => {
  const customAttributeMap = {
    date: 'date',
    time: 'time',
    text: 'plain_text',
    multiline_text: 'plain_text',
    file: 'plain_text',
    list: 'search_select',
    checkbox: 'search_select',
    person: 'search_select',
  };

  return customAttributeMap[key] || 'plain_text';
};

export const isACustomAttribute = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return attr.attribute_key === key;
  });
};

export const getCustomAttributeListDropdownValues = (
  customAttributes,
  type
) => {
  return customAttributes
    .find(attr => attr.attribute_key === type)
    .attribute_values.map(item => {
      return {
        id: item,
        name: item,
      };
    });
};

export const isCustomAttributeCheckbox = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === key && attr.attribute_display_type === 'checkbox'
    );
  });
};

export const isCustomAttributeList = (customAttributes, type) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === type && attr.attribute_display_type === 'list'
    );
  });
};

export const isCustomAttributePerson = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === key && attr.attribute_display_type === 'person'
    );
  });
};

export const getOperatorTypes = key => {
  const operatorMap = {
    list: OPERATOR_TYPES_1,
    text: OPERATOR_TYPES_3,
    multiline_text: OPERATOR_TYPES_2,
    file: OPERATOR_TYPES_2,
    person: OPERATOR_TYPES_1,
    number: OPERATOR_TYPES_7,
    personnel: OPERATOR_TYPES_7,
    link: OPERATOR_TYPES_1,
    date: OPERATOR_TYPES_4,
    time: OPERATOR_TYPES_9,
    checkbox: OPERATOR_TYPES_1,
  };

  return operatorMap[key] || OPERATOR_TYPES_1;
};

export const generateCustomAttributeTypes = (customAttributes, type) => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      inputType: getCustomAttributeInputType(attr.attribute_display_type),
      filterOperators: getOperatorTypes(attr.attribute_display_type),
      customAttributeType: type,
    };
  });
};

export const generateConditionOptions = (options, key = 'id') => {
  if (!options || !Array.isArray(options)) return [];
  return options.map(i => {
    return {
      id: i[key],
      name: i.title,
    };
  });
};

export const getActionOptions = ({
  agents,
  teams,
  labels,
  slaPolicies,
  eventLabels,
  type,
  addNoneToListFn,
  priorityOptions,
}) => {
  const actionsMap = {
    assign_agent: addNoneToListFn ? addNoneToListFn(agents) : agents,
    assign_team: addNoneToListFn ? addNoneToListFn(teams) : teams,
    send_email_to_team: teams,
    add_label: generateConditionOptions(labels, 'title'),
    add_smart_label: generateConditionOptions(labels, 'title'),
    add_smart_label_prototype: generateConditionOptions(labels, 'title'),
    remove_label: generateConditionOptions(labels, 'title'),
    change_priority: priorityOptions,
    add_sla: slaPolicies,
    set_event_label: generateConditionOptions(eventLabels),
  };
  return actionsMap[type];
};

export const getConditionOptions = ({
  agents,
  booleanFilterOptions,
  campaigns,
  contacts,
  countries,
  customAttributes,
  inboxes,
  languages,
  labels,
  statusFilterOptions,
  teams,
  type,
  priorityOptions,
  messageTypeOptions,
  messageImageTypeOptions,
  planningEventStatusOptions,
  eventLabels,
  eventLabelNoneOption,
  eventSourceOptions,
  bookingWidgets,
}) => {
  if (isCustomAttributeCheckbox(customAttributes, type)) {
    return booleanFilterOptions;
  }

  if (type === 'emoji_only') {
    return booleanFilterOptions;
  }

  if (isCustomAttributeList(customAttributes, type)) {
    return getCustomAttributeListDropdownValues(customAttributes, type);
  }

  if (isCustomAttributePerson(customAttributes, type)) {
    return agents;
  }

  const bookingWidgetOptions = (bookingWidgets || [])
    .filter(widget => widget.is_primary !== false)
    .map(widget => ({
      id: widget.id,
      name: widget.name || String(widget.id),
    }));

  const conditionFilterMaps = {
    status: statusFilterOptions,
    assignee_id: agents,
    contact: contacts,
    inbox_id: inboxes,
    team_id: teams,
    campaigns: generateConditionOptions(campaigns),
    browser_language: languages,
    conversation_language: languages,
    country_code: countries,
    message_type: messageTypeOptions,
    image_type: messageImageTypeOptions,
    priority: priorityOptions,
    labels: generateConditionOptions(labels, 'title'),
    event_status: planningEventStatusOptions,
    event_manager_id: agents,
    event_manager_team_id: teams,
    event_source: eventSourceOptions,
    booking_widget_id: bookingWidgetOptions,
    event_label_id: [
      ...(eventLabelNoneOption ? [eventLabelNoneOption] : []),
      ...generateConditionOptions(eventLabels),
    ],
  };

  return conditionFilterMaps[type];
};

export const getFileName = (action, files = []) => {
  const blobId = action.action_params[0];
  if (!blobId) return '';
  if (action.action_name === 'send_attachment') {
    const file = files.find(item => item.blob_id === blobId);
    if (file) return file.filename.toString();
  }
  return '';
};

export const getDefaultConditions = eventName => {
  if (eventName === 'message_created') {
    return structuredClone(DEFAULT_MESSAGE_CREATED_CONDITION);
  }
  if (
    eventName === 'conversation_opened' ||
    eventName === 'conversation_resolved'
  ) {
    return structuredClone(DEFAULT_CONVERSATION_CONDITION);
  }
  if (
    eventName === 'planning_event_created' ||
    eventName === 'planning_event_updated'
  ) {
    return structuredClone(DEFAULT_PLANNING_EVENT_CONDITION);
  }
  return structuredClone(DEFAULT_OTHER_CONDITION);
};

export const getDefaultActions = () => {
  return structuredClone(DEFAULT_ACTIONS);
};

export const filterCustomAttributes = customAttributes => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      type: attr.attribute_display_type,
    };
  });
};

export const getStandardAttributeInputType = (automationTypes, event, key) => {
  const condition = automationTypes[event].conditions.find(
    item => item.key === key
  );
  return condition?.inputType ?? 'plain_text';
};

export const generateAutomationPayload = payload => {
  const automation = JSON.parse(JSON.stringify(payload));
  automation.conditions[automation.conditions.length - 1].query_operator = null;
  automation.conditions = filterQueryGenerator(automation.conditions).payload;
  automation.actions = actionQueryGenerator(automation.actions);

  // Transform custom template composite keys into backend format
  automation.actions = automation.actions.map(action => {
    if (action.action_name.startsWith(CUSTOM_TEMPLATE_ACTION_PREFIX)) {
      const templateName = action.action_name.slice(
        CUSTOM_TEMPLATE_ACTION_PREFIX.length
      );
      return {
        ...action,
        action_name: 'send_custom_email_template',
        action_params: [templateName, ...(action.action_params || [])],
      };
    }
    return action;
  });

  return automation;
};

export const isCustomAttribute = (attrs, key) => {
  return attrs.find(attr => attr.key === key);
};

export const generateCustomAttributes = (
  // eslint-disable-next-line default-param-last
  conversationAttributes = [],
  // eslint-disable-next-line default-param-last
  contactAttributes = [],
  conversationlabel,
  contactlabel
) => {
  const customAttributes = [];
  if (conversationAttributes.length) {
    customAttributes.push(
      {
        key: `conversation_custom_attribute`,
        name: conversationlabel,
        disabled: true,
      },
      ...conversationAttributes
    );
  }
  if (contactAttributes.length) {
    customAttributes.push(
      {
        key: `contact_custom_attribute`,
        name: contactlabel,
        disabled: true,
      },
      ...contactAttributes
    );
  }
  return customAttributes;
};

/**
 * Get attributes for a given key from automation types.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {string} key - The key to get attributes for.
 * @returns {Array} Array of condition objects for the given key.
 */
export const getAttributes = (automationTypes, key) => {
  return automationTypes[key].conditions;
};

const PLANNING_ONLY_ACTION_KEYS = new Set([
  'apply_event_border_color',
  'clear_event_border_color',
  'apply_event_color',
  'clear_event_color',
  'lock_event_price',
  'unlock_event_price',
  'add_event_collaborators',
  'remove_event_collaborators',
  'set_event_label',
  'clear_event_label',
]);

/**
 * Action types available for a given automation event.
 * Planning events use the allow-list from AUTOMATIONS; other events exclude planning-only actions.
 */
export const getAutomationActionsForEvent = (
  automationTypes,
  eventName,
  allActionTypes,
  currentActionNames = []
) => {
  let filtered;
  const eventActions = automationTypes[eventName]?.actions;
  if (eventActions?.length) {
    const allowedKeys = new Set(eventActions.map(action => action.key));
    filtered = allActionTypes.filter(
      type =>
        allowedKeys.has(type.key) ||
        type.key?.startsWith(CUSTOM_TEMPLATE_ACTION_PREFIX)
    );
  } else {
    filtered = allActionTypes.filter(
      type => !PLANNING_ONLY_ACTION_KEYS.has(type.key)
    );
  }

  const missingKeys = currentActionNames.filter(
    key => key && !filtered.some(type => type.key === key)
  );
  if (missingKeys.length === 0) return filtered;

  const extras = allActionTypes.filter(type => missingKeys.includes(type.key));
  return [...filtered, ...extras];
};

/**
 * Get the automation type for a given key.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the automation type for.
 * @returns {Object} The automation type object.
 */
export const getAutomationType = (automationTypes, automation, key) => {
  return automationTypes[automation.event_name].conditions.find(
    condition => condition.key === key
  );
};

/**
 * Get the input type for a given key.
 * @param {Array} allCustomAttributes - Array of all custom attributes.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the input type for.
 * @returns {string} The input type.
 */
export const getInputType = (
  allCustomAttributes,
  automationTypes,
  automation,
  key
) => {
  const customAttribute = isACustomAttribute(allCustomAttributes, key);
  if (customAttribute) {
    return getCustomAttributeInputType(customAttribute.attribute_display_type);
  }
  const type = getAutomationType(automationTypes, automation, key);
  return type.inputType;
};

/**
 * Get operators for a given key.
 * @param {Array} allCustomAttributes - Array of all custom attributes.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} mode - The mode ('edit' or other).
 * @param {string} key - The key to get operators for.
 * @returns {Array} Array of operators.
 */
export const getOperators = (
  allCustomAttributes,
  automationTypes,
  automation,
  mode,
  key
) => {
  if (mode === 'edit') {
    const customAttribute = isACustomAttribute(allCustomAttributes, key);
    if (customAttribute) {
      return getOperatorTypes(customAttribute.attribute_display_type);
    }
  }
  const type = getAutomationType(automationTypes, automation, key);
  return type.filterOperators;
};

/**
 * Get the custom attribute type for a given key.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the custom attribute type for.
 * @returns {string} The custom attribute type.
 */
export const getCustomAttributeType = (automationTypes, automation, key) => {
  return automationTypes[automation.event_name].conditions.find(
    i => i.key === key
  ).customAttributeType;
};

/**
 * Determine if an action input should be shown.
 * @param {Array} automationActionTypes - Array of automation action type objects.
 * @param {string} action - The action to check.
 * @returns {boolean} True if the action input should be shown, false otherwise.
 */
export const showActionInput = (automationActionTypes, action) => {
  if (
    action === 'send_email_to_team' ||
    action === 'send_message' ||
    action === 'add_event_collaborators' ||
    action === 'remove_event_collaborators'
  )
    return false;
  const match = automationActionTypes.find(i => i.key === action);
  return !!match?.inputType;
};
